/**
 * Shared fa-IR number, money and Jalali-date formatting.
 *
 * These started life in `pages/guarantees/components/guaranteeTheme.ts`. They
 * moved here when the home page and the pay screen needed them too: the
 * alternative was three screens formatting the same toman figure three ways.
 * That was already visible — the AccessPay banner rendered Latin digits via
 * `Number(x).toLocaleString()` while the guarantees page rendered fa-IR, so
 * the same total looked like it came from two different apps.
 *
 * guaranteeTheme re-exports these, so existing imports keep working.
 */

export const faFull = new Intl.NumberFormat('fa-IR')

/**
 * Ungrouped — for years and small counts.
 *
 * `faFull.format(1405)` renders «۱٬۴۰۵», which reads as a number in the
 * thousands rather than a year.
 */
export const faPlain = new Intl.NumberFormat('fa-IR', { useGrouping: false })

export const toman = (value: number) =>
    `${faFull.format(Number(value || 0))} تومان`

/** Just the digits, no unit — for when the caller places «تومان» itself. */
export const money = (value: number) => faFull.format(Number(value || 0))

/**
 * «صندوق <name>» — with the word applied at most once.
 *
 * Every card that shows a fund used to build its own `` `صندوق ${item.name}` ``,
 * which assumes the stored name never carries the word itself. It does:
 * 2,562 of the 30,481 live funds (8.4%) are *named* «صندوق …» by their own
 * members, so those cards read «صندوق صندوق قرض الحسنه رز». On a title with
 * room for roughly sixteen characters a line, that is not a cosmetic slip —
 * it spends a whole word of the first line saying nothing.
 *
 * `startsWith` and not a `صندوق `-with-space test, because the names that
 * skip the space are the ones that most need skipping: «صندوق۱»,
 * «صندوق‌قرض‌الحسنه» (ZWNJ), «صندوقِ مهر مانا» (kasra), and the plain typo
 * «صندوقرص الحسنه مهدوی». It also swallows «صندوقچه …» and «صندوقی …» — 25
 * funds, a different word each time — and «صندوقچه دوستان» standing alone
 * still reads better than «صندوق صندوقچه دوستان».
 */
export const fundTitle = (name?: string | null) => {
    const trimmed = (name ?? '').trim()
    if (!trimmed) return 'صندوق'
    return trimmed.startsWith('صندوق') ? trimmed : `صندوق ${trimmed}`
}

/**
 * The half of the name that tells one fund from another.
 *
 * On a list of funds the word «صندوق» is the one thing every row agrees on —
 * the tab is called «صندوق‌ها», each row carries a fund avatar, and the
 * subtitle already says «پس‌انداز» or «قرعه‌کشی». Spending the first ~40px of
 * a 142px title on it buys nothing, so list titles drop it: whoever named
 * their fund «صندوق قرض الحسنه رز» gets «قرض الحسنه رز», and whoever named
 * theirs «قرض الحسنه ایثار» gets that unchanged. Same rule either way — the
 * word is never rendered as a leading label.
 *
 * Measured across all 30,481 live funds, wrapped at the card title's real
 * column: names cut off fall from 12.09% to 2.35% on one line, and from
 * 0.32% to 0.02% on two.
 *
 * The `\s` is load-bearing — only a «صندوق» that is its own word goes. These
 * are all real names, and none of them is the word plus a fund's name:
 * «صندوقچه دوستان» and «صندوقی مهر» are different words, «صندوق۱» and
 * «صندوق‌قرض‌الحسنه» (ZWNJ) and «صندوقِ مهِر مانا» (kasra) are single tokens
 * that would be left as «۱», «قرض‌الحسنه» and «مهِر مانا» by a looser rule.
 * A fund named exactly «صندوق» — 102 of them — keeps the word, because the
 * alternative is a card with no title at all.
 *
 * `fundTitle` is still the right call anywhere the word has to be spoken —
 * a screen-reader label, a sentence — where «قرض الحسنه رز» alone would not
 * parse as a fund.
 */
export const fundName = (name?: string | null) => {
    const trimmed = (name ?? '').trim()
    if (!trimmed) return 'صندوق'
    return trimmed.replace(/^صندوق\s+/, '').trim() || trimmed
}

const JALALI_MONTHS = [
    'فروردین',
    'اردیبهشت',
    'خرداد',
    'تیر',
    'مرداد',
    'شهریور',
    'مهر',
    'آبان',
    'آذر',
    'دی',
    'بهمن',
    'اسفند',
]

/**
 * «1405-04-29» becomes «۲۹ تیر ۱۴۰۵».
 *
 * The API sends the sortable YYYY-MM-DD Jalali form because it is also the
 * key that joins a timeline row to its data; the human form is built here.
 */
export const longJalali = (jalaliDate?: string | null) => {
    if (!jalaliDate) return ''
    const [year, month, day] = jalaliDate.split('-').map(Number)
    const monthName = JALALI_MONTHS[month - 1]
    if (!year || !day || !monthName) return ''
    return `${faPlain.format(day)} ${monthName} ${faPlain.format(year)}`
}

/** «۲۹ تیر» — the same date without the year, for in-year due dates. */
export const shortJalali = (jalaliDate?: string | null) => {
    if (!jalaliDate) return ''
    const [, month, day] = jalaliDate.split('-').map(Number)
    const monthName = JALALI_MONTHS[month - 1]
    if (!day || !monthName) return ''
    return `${faPlain.format(day)} ${monthName}`
}

/**
 * Human lateness/earliness for a payable row.
 *
 * Deliberately never returns an empty string for a due item — «امروز سررسید
 * است» is information, and a blank line where a date should be reads as a bug.
 */
export const dueLabel = (daysOverdue: number, isOverdue: boolean) => {
    if (isOverdue) {
        if (daysOverdue <= 0) return 'امروز سررسید است'
        return `${faPlain.format(daysOverdue)} روز تاخیر`
    }
    return ''
}
